import React from 'react';
import ReactECharts from 'echarts-for-react';
import {
  labelStyle,
  titleStyle
} from '@/lomavisSharedUi/atoms/charts/chartTextStyles';

export interface BarChartData {
  value: number;
  name: string;
  color?: string;
}

export interface LomavisBarChart1Props {
  title?: string;
  data: BarChartData[];
  allowZero?: boolean;
  style?: React.CSSProperties;
}

export const LomavisBarChart1: React.FC<LomavisBarChart1Props> = ({
  title,
  data,
  allowZero = false,
  style
}) => {
  // Only filter out entries with value 0 if there's at least one entry with value > 0
  const hasPositiveValues = data.some((item) => item.value > 0);
  const filteredData =
    hasPositiveValues && !allowZero
      ? data.filter((item) => item.value !== 0)
      : data;
  const sortedData = [...filteredData].sort((a, b) => a.value - b.value);
  const categories = sortedData.map((item) => item.name);

  // Check if there are any negative values
  const hasNegativeValues = filteredData.some((item) => item.value < 0);

  // Calculate min and max values for xAxis if there are negative values
  let xAxisMin, xAxisMax;
  if (hasNegativeValues) {
    const minValue = Math.min(...filteredData.map((item) => item.value));
    const maxValue = Math.max(...filteredData.map((item) => item.value));

    // Only center the y axis if the negative growth is larger than the positive growth
    const shouldCenter = Math.abs(minValue) > maxValue;

    if (shouldCenter) {
      // Center the axis
      const absMax = Math.max(Math.abs(minValue), Math.abs(maxValue));
      xAxisMin = -absMax;
      xAxisMax = absMax;
    } else {
      // Don't center, use actual min and max
      // If we have negative entries, the y axis should be at least at 25% of the positive maximum
      xAxisMin = minValue < 0 ? Math.min(minValue, -maxValue * 0.25) : minValue;
      xAxisMax = maxValue;
    }
  }

  const option: echarts.EChartsOption = {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c}'
    },
    legend: {
      show: false
    },
    grid: {
      left: 30,
      right: 30,
      top: 20, // Small top margin to allow bars and title in plot
      bottom: 40,
      containLabel: true
    },
    xAxis: {
      type: 'value',
      axisLabel: labelStyle,
      splitLine: {
        lineStyle: { color: '#dee2e6' }
      },
      min: hasNegativeValues ? xAxisMin : undefined,
      max: hasNegativeValues ? xAxisMax : undefined
    },
    yAxis: {
      type: 'category',
      data: categories,
      axisLabel: { show: false },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    series: [
      {
        type: 'bar',
        barWidth: '40%',
        barCategoryGap: '30%',
        data: sortedData.map((item) => ({
          value: item.value,
          name: item.name, // Needed for formatter {b}
          itemStyle: {
            color: item.color || '#5470c6',
            borderRadius: [5, 5, 5, 5]
          },
          label: {
            show: true,
            position: 'insideLeft',
            formatter: '{b}',
            color: '#E8E8E8',
            fontSize: filteredData.length > 5 ? 10 : 14,
            fontWeight: 600,
            fontFamily: 'Inter, sans-serif'
          }
        }))
      }
    ],
    graphic: title
      ? [
          {
            type: 'text',
            left: 0, // ✅ Align to left
            top: 10, // ✅ Distance from top edge
            z: 100,
            style: {
              text: title,
              ...titleStyle,
              align: 'left', // ✅ Align text content to the left
              verticalAlign: 'top',
              backgroundColor: '#ffffff',
              padding: [4, 8],
              borderRadius: 4
            }
          }
        ]
      : []
  };

  return (
    <div style={{ height: '100%', width: '100%', ...style }}>
      <ReactECharts option={option} style={{ height: '100%', width: '100%' }} />
    </div>
  );
};
